import { Utils, Logger, engineComponent } from "neo-common";
import { ComponentTypes } from "../ComponentTypes";
import { AppContext } from "../../../Common/AppContext";
import { DataManager } from "../../Managers/DataManager";
import { ITweenable, Tweens } from "../Effects/Tweens";

@engineComponent(ComponentTypes.Song)
export class Song implements ITweenable {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    public id: string;

    protected sound: PIXI.sound.Sound;

    protected alias: string;

    protected instance: PIXI.sound.IMediaInstance;

    protected group: string;

    private _originalVolume: number;

    protected volume: number;

    protected pan: number;

    protected fade: number;

    protected fadeStartTime: number;

    protected fadeEndTime: number;

    private _uid: string;

    /**
     * @property uid - unique identifier of element.
     * @returns {string}
     */
    public get uid(): string {
        return this._uid;
    }

    /**
     * @protected componentName - component type name.
     * @returns {string}
     */
    public get componentName(): string {
        return (this as any).__componentName__;
    }

    public get originalVolume(): number {
        return this._originalVolume;
    }

    constructor() {
        this._uid = `${this.componentName}_${Utils.generateUuid()}`;
    }

    //******************************************************************************************************************
    //  CREATE
    //******************************************************************************************************************

    public create(node: Element): void {
        this.id = node.nodeName;
        this.alias = node.hasAttribute(SongAttributes.ALIAS) ? node.getAttribute(SongAttributes.ALIAS) : null;
        this.instance = null;
        this.group = node.hasAttribute(SongAttributes.GROUP) ? node.getAttribute(SongAttributes.GROUP) : null;
        this._originalVolume = this.volume = node.hasAttribute(SongAttributes.VOLUME)
            ? parseFloat(node.getAttribute(SongAttributes.VOLUME))
            : 1;
        this.volume = node.hasAttribute(SongAttributes.VOLUME)
            ? parseFloat(node.getAttribute(SongAttributes.VOLUME))
            : 1;
        this.pan = node.hasAttribute(SongAttributes.PAN) ? parseFloat(node.getAttribute(SongAttributes.PAN)) : 0;
        this.fade = 0;
        this.fadeStartTime = 0;
        this.fadeEndTime = 0;

        if (node.hasAttribute(SongAttributes.SONG)) {
            if (PIXI.sound.exists(node.getAttribute(SongAttributes.SONG))) {
                this.sound = PIXI.sound.find(node.getAttribute(SongAttributes.SONG));
            } else {
                this.sound = null;

                if (DEBUG) {
                    this.log(`ERROR no sound with id: ${node.getAttribute("Song")}`);
                }
            }
        } else {
            this.sound = null;

            if (DEBUG) {
                this.log("ERROR no attribute: Song");
            }
        }
    }

    //******************************************************************************************************************
    //  PLAY
    //******************************************************************************************************************

    public play(loop: boolean, fadeIn: number, reset: boolean): void {
        if (this.group === "BackgroundMusic" && !AppContext.current.isBackgroundMusicEnabled) {
            return;
        }

        if (this.sound !== null && (reset || this.instance === null)) {
            this.sound.filters = this.pan === 0 ? null : [new PIXI.sound.filters.StereoFilter(this.pan)];

            if (fadeIn > 0) {
                this.fade = 1;
                this.fadeStartTime = new Date().getTime();
                this.fadeEndTime = this.fadeStartTime + fadeIn * 16.7;
            } else {
                this.fade = 0;
                this.fadeStartTime = 0;
                this.fadeEndTime = 0;
            }

            this.playInstance(loop);

            if (DEBUG) {
                if (loop) {
                    this.log("loop song " + this.id + " volume " + this.volume);
                } else {
                    this.log("play song " + this.id + " volume " + this.volume);
                }
            }
        }
    }

    protected playInstance(loop: boolean = true): void {
        this.stopInstance();

        let volume = this.volume;

        if (this.group === "BackgroundMusic" && !DataManager.BackSoundsPlayingFromGame) {
            volume = 0;
        } else if (this.fade > 0) {
            volume = this.getFadeInVolume();
        } else if (this.fade < 0) {
            volume = this.getFadeOutVolume();
        }

        this.instance = <any>(
            this.sound.play(this.alias !== null ? { sprite: this.alias, volume: volume } : { volume: volume })
        );
        this.instance.on("end", loop ? this.playInstance : this.stopInstance, this);

        if (this.fade !== 0) {
            this.instance.on("progress", this.fade === 1 ? this.onFadeIn : this.onFadeOut, this);
        }
    }

    public isPlaying(): boolean {
        return !!this.instance;
    }

    protected onFadeIn(): void {
        const volume = this.getFadeInVolume();

        try {
            this.instance.volume =
                this.group === "BackgroundMusic" && !DataManager.BackSoundsPlayingFromGame ? 0 : volume;
        } catch (e) {}

        if (volume === this.volume) {
            this.instance.off("progress", this.onFadeIn, this);
        }
    }

    protected getFadeInVolume(): number {
        const duration = this.fadeEndTime - this.fadeStartTime;
        const progress = new Date().getTime() - this.fadeStartTime;

        return this.volume * Math.min(progress / duration, 1);
    }

    //******************************************************************************************************************
    //  STOP
    //******************************************************************************************************************

    public stop(fadeOut: number): void {
        if (this.instance != null) {
            if (fadeOut > 0) {
                this.fade = -1;
                this.fadeStartTime = new Date().getTime();
                this.fadeEndTime = this.fadeStartTime + fadeOut * 16.7;

                this.instance.off("progress", this.onFadeIn, this);
                this.instance.on("progress", this.onFadeOut, this);
            } else {
                this.stopInstance();
            }
            if (DEBUG) {
                this.log("stop song " + this.id);
            }
        }
    }

    protected stopInstance(): void {
        if (this.instance != null) {
            this.instance.off("progress", this.onFadeIn, this);
            this.instance.off("progress", this.onFadeOut, this);
            this.instance.off("end", this.playInstance, this);
            this.instance.off("end", this.stopInstance, this);
            this.instance.stop();
            this.instance = null;
        }
    }

    protected onFadeOut(): void {
        const volume = this.getFadeOutVolume();

        try {
            this.instance.volume =
                this.group === "BackgroundMusic" && !DataManager.BackSoundsPlayingFromGame ? 0 : volume;
        } catch (e) {}

        if (volume === 0) {
            this.stopInstance();
        }
    }

    public pause(): void {
        if (this.instance != null) {
            this.sound.pause();
        }
    }

    public resume(): void {
        if (this.instance != null) {
            this.sound.resume();
        }
    }

    protected getFadeOutVolume(): number {
        const duration = this.fadeEndTime - this.fadeStartTime;
        const progress = new Date().getTime() - this.fadeStartTime;

        return this.volume - this.volume * Math.min(progress / duration, 1);
    }

    //******************************************************************************************************************
    //  TOGGLE
    //******************************************************************************************************************

    public toggleGroup(group: string, toggle: boolean = false): void {
        if (this.group === group && this.instance != null) {
            try {
                this.instance.volume = toggle ? this.volume : 0;
            } catch (e) {}
        }
    }

    //******************************************************************************************************************
    //  LOG
    //******************************************************************************************************************

    protected log(s: string): void {
        if (DEBUG) {
            const compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }

    /**
     * Set volume level with possibility to fade in or fade out the song.
     * @param volume
     * @param fadeDuration When target volume is bigger than current volume, make fade in. Else make fade out.
     */
    public setVolume(volume: number, fadeDuration?: number): void {
        if (volume != this.volume) {
            if (DEBUG) {
                if (fadeDuration != null) {
                    this.log("setVolume to " + volume + " of song " + this.id + " with fadeDuration " + fadeDuration);
                } else {
                    this.log("setVolume to " + volume + " of song " + this.id);
                }
            }

            if (fadeDuration != null) {
                this.fadeVolume(volume, fadeDuration);
            } else {
                Tweens.removeTweens(this, "volume"); // Kill previously tweened volume.

                this.volume = volume;

                if (this.instance != null) {
                    this.instance.volume = volume;
                }
            }
        }
    }

    /**
     * Re-set volume level with possibility to fade in or fade out the song.
     * @param fadeDuration When target volume is bigger than current volume, make fade in. Else make fade out.
     */
    public resetVolume(fadeDuration?: number): void {
        this.setVolume(this.originalVolume, fadeDuration);
    }

    /**
     *
     * @param volume
     * @param fadeDuration
     */
    private fadeVolume(volume: number, fadeDuration: number): void {
        Tweens.removeTweens(this, "volume");
        Tweens.customTween(this, {
            tween: "volume",
            tweenValues: [this.volume, volume],
            tweenDuration: [fadeDuration],
            callbackType: "onUpdate",
            callbackFunction: () => {
                this.onFadeSong(this.volume);
            },
        });
    }

    /**
     *
     * @param volume
     */
    private onFadeSong(volume: number): void {
        if (volume != null) {
            this.volume = volume;

            if (this.instance != null) {
                this.instance.volume = volume;
            }
        }
    }
}

export const SongAttributes = {
    SONG: "Song",
    ALIAS: "Alias",
    GROUP: "Group",
    VOLUME: "Volume",
    PAN: "Pan",
    PLAY: "Play",
    LOOP: "Loop",
    STOP: "Stop",
    FADEIN: "FadeIn",
    FADEOUT: "FadeOut",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Sounds/Song.ts
 **/